import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../ui/confirm';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from '../../lib/toast';
import { Copy, ExternalLink, Lock, Wifi, WifiOff, Smartphone, Trash2, Plus, X } from 'lucide-react';
import {
  SettingsSection,
  SettingsRow,
  SettingsHeader,
  StatusBadge
} from './SettingsUI';

/**
 * Remote Access Settings - Apple Home-Style UX
 *
 * Design Principles:
 * - Card-based layout with clear visual hierarchy
 * - Touch-friendly controls (min 44px touch targets)
 * - Icon + status indicators for quick recognition
 * - Lock icon indicates immutable configuration
 * - Service controls in 2-column grid for mobile
 * - QR code prominently displayed for easy scanning
 * - Calm, minimal design with proper spacing
 */

const QRCodeDisplay = ({ value }) => {
  const [QRCode, setQRCode] = useState(null);

  useEffect(() => {
    import('qrcode.react').then(module => {
      setQRCode(() => module.QRCodeSVG);
    }).catch(() => {
      setQRCode(null);
    });
  }, []);

  if (!QRCode) {
    return (
      <div className="w-48 h-48 flex items-center justify-center bg-gray-100 rounded">
        <span className="text-xs text-muted-foreground text-center px-4">
          QR Code library not available
        </span>
      </div>
    );
  }

  return <QRCode value={value} size={192} level="M" />;
};

const formatRelativeTime = (isoString) => {
  if (!isoString) return 'Never';
  const date = new Date(isoString);
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'Just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 2592000) return `${Math.floor(diffSec / 86400)}d ago`;
  return date.toLocaleDateString();
};

const FRPSettings = () => {
  const { apiRequest, listApiTokens, createApiToken, revokeApiToken } = useAuth();
  const confirm = useConfirm();
  const [frpStatus, setFrpStatus] = useState(null);
  const [frpUrl, setFrpUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configuring, setConfiguring] = useState(false);
  const [error, setError] = useState(null);
  const [showConfigForm, setShowConfigForm] = useState(false);

  const [formData, setFormData] = useState({
    hubName: '',
    token: ''
  });

  // Mobile API tokens state
  const [apiTokens, setApiTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [newTokenRole, setNewTokenRole] = useState('user');
  const [creatingToken, setCreatingToken] = useState(false);
  const [generatedToken, setGeneratedToken] = useState(null); // { token, name, id, role } shown in modal

  const fetchFRPStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const {response, data} = await apiRequest('/api/frp/status');

      if (response.ok) {
        setFrpStatus(data.data);
        setFrpUrl(data.data.frpUrl);

        // Store hubName if available
        if (data.data.hubName) {
          setFormData(prev => ({ ...prev, hubName: data.data.hubName }));
        }
      } else {
        setError('Failed to fetch FRP status');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiRequest]);

  useEffect(() => {
    fetchFRPStatus();
  }, [fetchFRPStatus]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleConfigure = async (e) => {
    e.preventDefault();

    try {
      setConfiguring(true);
      setError(null);

      const {response, data} = await apiRequest('/api/frp/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      console.log('API Response status:', response.status, response.ok);
      console.log('API Response data:', data);

      if (response.ok) {
        console.log('FRP Registration Success:', data);
        setShowConfigForm(false);
        setError(null);
        setFormData({ hubName: '', token: '' });
        // Refresh status from backend to sync all state
        await fetchFRPStatus();
      } else {
        console.log('FRP Registration Failed:', response.status, response.statusText);
        setError(data.error || 'Failed to register hub for remote access');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setConfiguring(false);
    }
  };

  const handleServiceControl = async (action) => {
    try {
      setError(null);

      const {response, data} = await apiRequest(`/api/frp/${action}`, {
        method: 'POST'
      });

      if (response.ok) {
        await fetchFRPStatus();
      } else {
        setError(data.error || `Failed to ${action} FRP service`);
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success('Copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleOpenUrl = () => {
    window.open(frpUrl, '_blank', 'noopener,noreferrer');
  };

  // ===== Mobile API Tokens =====
  const fetchApiTokens = useCallback(async () => {
    setTokensLoading(true);
    const result = await listApiTokens();
    if (result.success) {
      setApiTokens(result.tokens);
    }
    setTokensLoading(false);
  }, [listApiTokens]);

  useEffect(() => {
    fetchApiTokens();
  }, [fetchApiTokens]);

  const handleCreateToken = async () => {
    const name = newTokenName.trim() || 'Mobile App';
    setCreatingToken(true);
    const result = await createApiToken(name, newTokenRole);
    setCreatingToken(false);

    if (result.success) {
      setGeneratedToken({ id: result.id, token: result.token, name: result.name, role: result.role });
      setNewTokenName('');
      setNewTokenRole('user');
      await fetchApiTokens();
    } else {
      toast.error(result.error || 'Failed to create token');
    }
  };

  const handleRevokeToken = async (tokenId, tokenName) => {
    const ok = await confirm({
      title: 'Revoke Token',
      description: `Revoke token "${tokenName}"? This cannot be undone and will immediately disconnect any device using it.`,
      confirmText: 'Revoke',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    const result = await revokeApiToken(tokenId);
    if (result.success) {
      toast.success('Token revoked');
      await fetchApiTokens();
    } else {
      toast.error(result.error || 'Failed to revoke token');
    }
  };

  const buildMobileUrl = (plainToken) => `${frpUrl}?token=${plainToken}`;

  if (loading) {
    return (
      <div className="py-4">
        <SettingsHeader title="Remote Access" />
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="py-4 space-y-6">
      <SettingsHeader
        title="Remote Access"
        description="Configure FRP for secure remote access"
      />

      {error && (
        <div className="px-4">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      )}

      {frpUrl && (
        <div className="px-4">
          <Card className="border border-border/40 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                    <Lock className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Remote Access</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {frpStatus?.service?.isActive ? 'Connected' : 'Disconnected'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {frpStatus?.service?.isActive ? (
                    <Wifi className="w-5 h-5 text-green-500" />
                  ) : (
                    <WifiOff className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Hub URL</p>
                    <button
                      onClick={handleOpenUrl}
                      className="font-mono text-xs text-primary hover:underline break-all text-left"
                    >
                      {frpUrl}
                    </button>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => copyToClipboard(frpUrl)}
                      className="h-8 w-8 shrink-0"
                      title="Copy URL"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleOpenUrl}
                      className="h-8 w-8 shrink-0"
                      title="Open in browser"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Mobile App Tokens */}
      {frpUrl && (
        <div className="px-4">
          <Card className="border border-border/40 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                    <Smartphone className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Mobile App Access</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Generate tokens to pair mobile devices
                    </p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0 space-y-4">
              {/* Token list */}
              {tokensLoading ? (
                <div className="text-center py-4 text-sm text-muted-foreground">Loading...</div>
              ) : apiTokens.length === 0 ? (
                <div className="text-center py-6 text-sm text-muted-foreground">
                  No mobile tokens yet. Generate one below to pair a device.
                </div>
              ) : (
                <div className="space-y-2">
                  {apiTokens.map((tok) => (
                    <div
                      key={tok.id}
                      className="flex items-center justify-between gap-2 p-3 bg-muted/30 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium truncate">{tok.name}</p>
                          <span
                            className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${
                              tok.role === 'admin'
                                ? 'bg-destructive/10 text-destructive'
                                : 'bg-primary/10 text-primary'
                            }`}
                          >
                            {tok.role || 'user'}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Last used: {formatRelativeTime(tok.lastUsedAt)}
                          <span className="mx-1.5">·</span>
                          Created: {formatRelativeTime(tok.createdAt)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRevokeToken(tok.id, tok.name)}
                        className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
                        title="Revoke token"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Create new token */}
              <div className="space-y-2 pt-1">
                <div className="flex flex-wrap gap-2">
                  <Input
                    type="text"
                    value={newTokenName}
                    onChange={(e) => setNewTokenName(e.target.value)}
                    placeholder="Token name (e.g. iPhone)"
                    className="h-10 flex-1 min-w-[160px]"
                    disabled={creatingToken}
                  />
                  <select
                    value={newTokenRole}
                    onChange={(e) => setNewTokenRole(e.target.value)}
                    disabled={creatingToken}
                    className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                  <Button
                    onClick={handleCreateToken}
                    disabled={creatingToken}
                    className="h-10"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {creatingToken ? 'Creating...' : 'Generate'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  <strong>User</strong> tokens can control devices but cannot modify settings, manage credentials, or delete data.
                  <strong className="ml-1">Admin</strong> tokens have full access. The token value is shown only once.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* New token QR modal */}
      {generatedToken && frpUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-2xl border border-border/60 shadow-xl w-full max-w-md max-h-[90dvh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border/60">
              <div>
                <h3 className="text-lg font-semibold">Pair Mobile Device</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Scan this QR code from the Mirfa app
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setGeneratedToken(null)}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-center bg-white rounded-xl p-4 border border-border/20">
                <QRCodeDisplay value={buildMobileUrl(generatedToken.token)} />
              </div>

              <Alert>
                <AlertDescription className="text-xs">
                  This token is shown only once. If you lose it, generate a new one and revoke this token.
                </AlertDescription>
              </Alert>

              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Pairing URL</p>
                <p className="font-mono text-xs break-all">{buildMobileUrl(generatedToken.token)}</p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(buildMobileUrl(generatedToken.token))}
                  className="flex-1 h-11"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy URL
                </Button>
                <Button
                  onClick={() => setGeneratedToken(null)}
                  className="flex-1 h-11"
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {frpStatus?.hasConfiguration && (
        <SettingsSection title="Remote Access">
          <SettingsRow
            label="Status"
            accessory={<StatusBadge status={frpStatus.service.isActive ? 'active' : 'inactive'} />}
          />
          {formData.hubName && (
            <SettingsRow
              label="Hub Name"
              accessory={
                <div className="flex items-center gap-2">
                  <span className="text-sm font-mono">{formData.hubName}</span>
                  <span className="text-xs text-muted-foreground">(locked)</span>
                </div>
              }
            />
          )}
        </SettingsSection>
      )}

      <div className="px-4 space-y-3">
        {!frpStatus?.hasConfiguration && (
          <Button onClick={() => setShowConfigForm(true)} className="w-full h-12 text-base">
            Configure Remote Access
          </Button>
        )}

        {frpStatus?.hasConfiguration && (
          <div className="grid grid-cols-2 gap-3">
            {!frpStatus.service.isActive ? (
              <Button
                onClick={() => handleServiceControl('start')}
                className="h-11 text-base"
              >
                Start
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => handleServiceControl('stop')}
                className="h-11 text-base"
              >
                Stop
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => handleServiceControl('restart')}
              className="h-11 text-base"
            >
              Restart
            </Button>
          </div>
        )}
      </div>


      {showConfigForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-2xl border border-border/60 shadow-xl w-full max-w-md max-h-[90dvh] overflow-y-auto">
            <div className="p-6 border-b border-border/60">
              <h3 className="text-lg font-semibold">Enable Remote Access</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Register this hub with the central server
              </p>
            </div>
            <form onSubmit={handleConfigure} className="p-6 space-y-5">
              <div className="space-y-3">
                <Label htmlFor="hubName" className="text-sm font-medium">
                  Hub Name {frpUrl && <span className="text-muted-foreground">(locked)</span>}
                </Label>
                {frpUrl ? (
                  <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg border border-border/40">
                    <span className="text-sm font-mono flex-1">{formData.hubName || '(configured)'}</span>
                  </div>
                ) : (
                  <Input
                    type="text"
                    id="hubName"
                    name="hubName"
                    value={formData.hubName}
                    onChange={handleInputChange}
                    placeholder="living-room-hub"
                    className="h-11"
                    required
                  />
                )}
                {!frpUrl && (
                  <p className="text-xs text-muted-foreground">
                    Lowercase, use hyphens instead of spaces
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <Label htmlFor="token" className="text-sm font-medium">Authentication Token</Label>
                <Input
                  type="password"
                  id="token"
                  name="token"
                  value={formData.token}
                  onChange={handleInputChange}
                  placeholder="Your authentication token"
                  className="h-11"
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowConfigForm(false);
                    setFormData({ hubName: '', token: '' });
                  }}
                  disabled={configuring}
                  className="flex-1 h-11"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={configuring} className="flex-1 h-11">
                  {configuring ? 'Registering...' : 'Register'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <SettingsSection title="About">
        <div className="px-4 py-3 text-sm text-muted-foreground space-y-1">
          
          <p>All traffic is encrypted and authenticated.</p>
        </div>
      </SettingsSection>
    </div>
  );
};

export default FRPSettings;
